'use client';
import { bg0 } from '@osmosis/tailwind-ui/styles/background';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';

import React, { useEffect } from 'react';

import PlaylistHeader from './PlaylistHeader';
import PlaylistsSideNav from './PlaylistsSideNav';
import PlaylistVideos from './PlaylistVideos';
import {
  PlaylistGroupProps,
  PlaylistProps,
} from '@osmosis/shared/types/playlist';
import { useUserContext } from '../../../context/UserContext';
import { useActivePlaylist } from '../../../hooks/useActivePlaylist';
import EmptyPlaylists from './EmptyPlaylists';
import useModal from '../../../hooks/useModal';
import Modal from '../../../components/Modal';
import NewPlaylistModalBox from './NewPlaylistModalBox';
import { useIntl } from 'react-intl';
import { useFeatureFlagsContext } from '../../../context/FeatureFlagsContext';
import SkipLink from '../../../components/SkipLink';
import ExpiredUserLockoutModal from '../../../components/learn/ExpiredUserModal';
import { ExpiredUserModalType } from '../../../constants/ExpiredUserModalConfig';
import { usePlaylistContext } from '../../../context/PlaylistProvider';

interface Props {
  groups: PlaylistGroupProps[];
  playlist?: PlaylistProps;
}

/**
 * Return PlaylistPage Component.
 */
const PlaylistPage = (props: Props): JSX.Element => {
  const { groups: initialGroups, playlist: initialPlaylist } = props;
  const intl = useIntl();
  const { user, hasPrime } = useUserContext();

  const newPlaylistModal = useModal();
  const playlistNonPrimeModal = useModal();

  const { groups: contextGroups, warmPlaylistCache } = usePlaylistContext();
  const groups = contextGroups.length ? contextGroups : initialGroups;

  // Use the already-retrieved playlist from the cache when available,
  // otherwise fetch only the active playlist.
  const playlist = useActivePlaylist(initialPlaylist?.url, initialPlaylist);

  // After loading the active playlist, fetch all playlists in the background
  useEffect(() => {
    if (playlist) {
      warmPlaylistCache();
    }
  }, [playlist, warmPlaylistCache]);

  const retiredFeatureFlag = useFeatureFlagsContext().isActive(
    'cs-retire-and-redirect',
  );

  return (
    <Box>
      <SkipLink
        linkText="the playlist"
        style={{ background: 'white', color: 'black' }}
        linkUrl={[
          'play-all-playlist-btn',
          'empty-playlist-new-playlist-btn',
          'thumbnail-container',
        ]}
      />

      <Modal
        ariaModal={true}
        role="dialog"
        hide={newPlaylistModal.hide}
        isVisible={newPlaylistModal.isVisible}
        headerContent={intl.formatMessage({
          defaultMessage: 'New Playlist',
          id: 'playlist.new-playlist-modal-box.heading',
        })}
        hasHeader={true}
      >
        <NewPlaylistModalBox
          handleClose={newPlaylistModal.hide}
          showNonPrimeModal={playlistNonPrimeModal.show}
        />
      </Modal>

      {!hasPrime && (
        <ExpiredUserLockoutModal
          {...playlistNonPrimeModal}
          modalType={ExpiredUserModalType.PLAYLIST}
        />
      )}

      <PlaylistHeader />

      <Flex className={`${bg0} flex-col flex-col lg:flex-row`}>
        {user ? (
          <>
            <Box className={`min-w-[275px]`}>
              <PlaylistsSideNav
                playlistGroups={groups}
                newPlaylistModal={newPlaylistModal}
              />
            </Box>
            <Box className={`p-4 flex-1`}>
              {playlist ? (
                <PlaylistVideos
                  playlist={playlist}
                  retireFeatureFlag={retiredFeatureFlag}
                />
              ) : (
                <EmptyPlaylists newPlaylistModal={newPlaylistModal} />
              )}
            </Box>
          </>
        ) : (
          <Box className={`p-4 flex-16 max-w-[1400px] mx-auto`}>
            {playlist ? (
              <PlaylistVideos
                playlist={playlist}
                retireFeatureFlag={retiredFeatureFlag}
              />
            ) : (
              <Box
                data-testid="playlist-empty-page"
                className={`min-h-[500px]`}
              />
            )}
          </Box>
        )}
      </Flex>
    </Box>
  );
};

export default PlaylistPage;
